// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';

// Helpers
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getImageUrl,
} from '@vroskus/library-helpers/dist/browser';
import {
  shortenText,
} from 'common.helpers/dist/browser';
import config from '../../../../config';

// Services
import Services from '../../../../services';

// Components
import Loader from '../../../layout/Loader';
import Section from '../../../layout/Section';

// Types
type $Props = $Translate & {
  item: $Model<'Regatta'>;
};

type $State = {
  data: Array<$Model<'RegattaContact'>>;
  loading: boolean;
};

class ContactsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getRegattaContactsSectionData({
        RegattaId: item.id,
      });

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (data.RegattaId === item.id && data.modelName === 'RegattaContact') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  renderOrganizer() {
    const {
      item,
      t,
    } = this.props;

    return (item.OrganizerContact ? (
      <div
        className={'rounded-lg bg-white overflow-hidden relative max-w-sm flex items-center p-5'}
        key={'organizer'}
      >
        <div className={'flex flex-col'}>
          <strong
            className={'uppercase'}
            title={t('Name')}
          >
            {item.OrganizerName}
          </strong>
          <h4
            className={'h4 uppercase'}
            title={t('Role')}
          >
            {t('Organizer')}
          </h4>
          <span
            className={'text-gray-600'}
            style={{
              whiteSpace: 'pre-wrap',
            }}
            title={t('Contact data')}
          >
            {item.OrganizerContact}
          </span>
        </div>
      </div>
    ) : null);
  }

  renderContact(dataItem: $Model<'RegattaContact'>) {
    const {
      t,
    } = this.props;

    const imageUrl: string = getImageUrl({
      cdnUrl: config.CDN_URL,
      image: dataItem.Image,
      size: 'mid',
      undefinedImage: 'contact.jpg',
    });

    return (
      <div
        className={'rounded-lg bg-white overflow-hidden relative max-w-sm flex items-center p-5'}
        key={dataItem.id}
      >
        <img
          alt={t('Contact')}
          className={'-mx-5 -my-5 max-h-32'}
          src={imageUrl}
        />
        <div className={'flex flex-col pl-10'}>
          <strong
            className={'uppercase'}
            title={t('Name')}
          >
            {dataItem.Name}
          </strong>
          <h4
            className={'h4 uppercase'}
            title={`${t('Role')}: ${dataItem.Role}`}
          >
            {shortenText(
              dataItem.Role,
              38,
            )}
          </h4>
          <span className={'text-gray-600'}>
            {dataItem.Email && (
              <a
                href={`mainto:${dataItem.Email}`}
                title={dataItem.Email}
              >
                <em className={'la la-envelope'} />
              </a>
            )}
            {(dataItem.Email && dataItem.Phone) && (
              <span className={'text-gray-400'}>
                {' | '}
              </span>
            )}
            {dataItem.Phone && (
              <a href={`tel:${dataItem.Phone}`}>
                <em className={'la la-phone mr-1'} />
                {dataItem.Phone}
              </a>
            )}
          </span>
        </div>
      </div>
    );
  }

  renderContacts() {
    const {
      data,
    } = this.state;

    return data.map((dataItem: $Model<'RegattaContact'>) => this.renderContact(dataItem));
  }

  render() {
    const {
      item,
      t,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    return (item.OrganizerContact || data.length > 0 ? (
      <Section
        id={webSections[webPages.regattaPage].contacts}
        title={t('Contacts')}
        transparent
      >
        <Loader
          className={'grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 gap-3 -m-5 mt-3'}
          fade
          loading={loading === true}
        >
          <>
            {this.renderOrganizer()}
            {this.renderContacts()}
          </>
        </Loader>
      </Section>
    ) : null);
  }
}

export default ContactsSectionView;
